import React, { useEffect, useState, useRef } from "react";
import styles from "./TaskDetailPanel.module.css";
import CrossButton from "../Customs/Button/CrossButton";
import AsyncSelect from "react-select/async";
import { FaPaperPlane, FaRegFileAlt, FaDownload, FaPaperclip } from "react-icons/fa";
import WebService from "../Services/WebService";
import { mediaUrl } from "../Services/mediaUrl";
import { toast } from "react-toastify";

// --- INTERFACES ---
interface Task {
  id: number;
  task_id: string;
  subject: string;
  Description?: string;
  priority: "HIGH" | "MEDIUM" | "LOW";
  status: "COMPLETED" | "PROCESSING" | "PAUSED" | "PENDING" | "IN_PROGRESS" | "CANCELED";
  weightage: number;
  start_date: string;
  end_date: string;
  created_by_username: string;
  assign_to: number;
  assign_to_username: string;
  tags?: string;
  time_spent?: number;
  timer_started_at?: string;
  timer_running?: boolean;
  sale_order_id?: number;
  images: { file_name: string; url?: string }[];
  comments: {
    comment_id: number;
    task_id: number;
    comment: string;
    created_at?: string;
    commented_by: number;
    commented_by_username?: string;
    image?: string;  
    file_name?: string; 
  }[];
}

interface TaskDetailPanelProps {
  open: boolean;
  taskId: number | null;
  mode: "view" | "edit";
  onClose: () => void;
  refreshTasks?: () => void;
  canWrite?: boolean;
  canDelete?: boolean;
  canRead?: boolean;
  statusOnly?: boolean;
}



// Style helpers
const priorityColors: Record<string, string> = {
  HIGH: styles.priorityHigh,
  MEDIUM: styles.priorityMedium,
  LOW: styles.priorityLow,
};
const statusColors: Record<string, string> = {
  PENDING: styles.statusPending,
  PROCESSING: styles.statusProcessing,
  IN_PROGRESS: styles.statusInProgress,
  IN_PROCESSING: styles.statusProcessing,
  PAUSED: styles.statusPaused,
  COMPLETED: styles.statusCompleted,
  CANCELED: styles.statusPaused,
};

const canonicalTaskStatus = (status?: string) => {
  const key = (status || "PENDING").toUpperCase().replace(/\s/g, "_");
  if (key === "IN_PROGRESS" || key === "IN_PROCESSING") return "PROCESSING";
  if (key === "CANCELLED") return "CANCELED";
  return key;
};

function getInitials(name?: string): string {
  if (!name) return "?";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}
const toIso = (date: string) =>
  !date ? "" : date.length > 10 ? date : `${date}T00:00:00Z`;

const isImage = (file_name?: string) => {
  if (!file_name) return false;
  
  const clean = file_name.split('?')[0].split('#')[0].toLowerCase();
  return /\.(jpg|jpeg|png|gif|bmp|webp|svg)$/i.test(clean);
};



const getCommentImageUrl = (image?: string): string => mediaUrl(image);


const TaskDetailPanel: React.FC<TaskDetailPanelProps> = ({
  open, taskId, mode, onClose, refreshTasks,
  canWrite = false,
  canDelete = false,
  canRead = true,
  statusOnly = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [task, setTask] = useState<Task | null>(null);
  const [error, setError] = useState<string>("");
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState("");
  // const [employees, setEmployees] = useState<Employee[]>([]);
  const [comment, setComment] = useState("");
  const [commentFile, setCommentFile] = useState<File | null>(null);
  const [commentLoading, setCommentLoading] = useState(false);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [fetchedAt, setFetchedAt] = useState(Date.now());
  const [nowTick, setNowTick] = useState(Date.now());
  const fileInputRef = useRef<HTMLInputElement>(null);
   const formatDateTime = (dateStr?: string) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return isNaN(d.getTime()) ? dateStr : d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};
  const isFullEdit = mode === "edit" && !statusOnly;
  const isStatusEdit = mode === "edit";
  const canComment = isStatusEdit && (canWrite || statusOnly);

  // --- Effective time spent for the progress bar (backend total + elapsed since fetch) ---
const liveSpent = (() => {
  const base = Number(task?.time_spent ?? 0);
  if (task?.timer_running) {
    return base + (nowTick - fetchedAt) / 3_600_000;
  }
  return base;
})();
const effectiveSpent = liveSpent;

const capacity =
  isFullEdit
    ? (editTask?.weightage ?? task?.weightage ?? 1)
    : (task?.weightage ?? 1);

const pct = Math.min((effectiveSpent / capacity) * 100, 100);
const overflowPct = effectiveSpent > capacity
  ? ((effectiveSpent - capacity) / capacity) * 100
  : 0;
 function buildApiUrl(base: string, params: Record<string, string | number | undefined>) {
  const q = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
    .join("&");
  return q ? `${base}?${q}` : base;
}

// (optional) tiny debounce
function debounce<T extends (...args: any[]) => void>(fn: T, ms = 300) {
  let t: any;
  return (...args: Parameters<T>) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}
// AsyncSelect loader
const loadEmployeeOptions = (inputValue: string, callback: (opts: {value:number; label:string}[]) => void) => {
  const action = buildApiUrl("/v1/door-management/employees", {
    page_number: 1,
    page_size: 10,
    sort_order: "asc",
    name: inputValue?.trim() || undefined,
  });
  WebService.getAPI({ action })
    .then((res: any) => {
      const list = res?.data?.employees ?? [];
      callback(list.map((e: any) => ({ value: e.user_id, label: e.username })));
    })
    .catch(() => callback([]));
};
const debouncedLoadEmployeeOptions = React.useMemo(
  () => debounce(loadEmployeeOptions, 300),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []
);
  // Fetch Task
  useEffect(() => {
    if (open && taskId) {
      setLoading(true);
      setError("");
      WebService.getAPI({ action: `/v1/door-management/tasks?task_id=${taskId}` })
        .then((res: any) => {
          const data = Array.isArray(res.data?.tasks)
            ? res.data.tasks[0]
            : res.data.task || res.data;
          const normalized = {
            ...data,
            status: canonicalTaskStatus(data?.status),
            Description: data?.Description || data?.description,
          };
          setTask(normalized);
          setEditTask(normalized);
          setFetchedAt(Date.now());
          setNowTick(Date.now());
        })
        .catch(() => setError("Failed to fetch task details."))
        .finally(() => setLoading(false));
    }
  }, [open, taskId]);

  useEffect(() => {
    if (!task?.timer_running) return;
    const id = window.setInterval(() => setNowTick(Date.now()), 30000);
    return () => window.clearInterval(id);
  }, [task?.timer_running]);
  
type FieldErrors = Partial<
  Record<
    | "subject"
    | "description"
    | "status"
    | "priority"
    | "weightage"
    | "start_date"
    | "end_date"
    | "assign_to"
    | "time_spent",
    string
  >
>;

const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
const clearErr = (key: keyof FieldErrors) =>
  setFieldErrors(prev => {
    const { [key]: _, ...rest } = prev;
    return rest;
  });

          const validateEdit = (t: Task): FieldErrors => {
            const errs: FieldErrors = {};
            if (statusOnly) {
              if (!t.status) errs.status = "Status is required";
              return errs;
            }

            // Title
            if (!t.subject?.trim()) errs.subject = "Task title is required";

            // Description (Task has 'Description' with capital D)
            if (!t.Description?.trim()) {
              errs.description = "Description is required";
            }
            const sd = t.start_date?.slice(0, 10);
            const ed = t.end_date?.slice(0, 10);
            if (!sd) errs.start_date = "Start date is required";
            if (!ed) errs.end_date = "End date is required";
            if (sd && ed && new Date(sd) > new Date(ed)) {
              errs.start_date = "Start must be before end";
              errs.end_date = "End must be after start";
            }
            if (!t.assign_to) errs.assign_to = "Assignee is required";
            const w = Number(t.weightage);
            if (!(w > 0)) errs.weightage = "Weightage must be greater than 0";
            return errs;
          };

  // Fetch Employees
  // useEffect(() => {
  //   if (open && mode === "edit" && canWrite) {
  //     WebService.getAPI({ action: "/v1/door-management/employees" })
  //       .then((res: any) => setEmployees(res.data?.employees || []))
  //       .catch(() => setEmployees([]));
  //   }
  // }, [open, mode, canWrite]);

  // PATCH Task
const handleUpdateTask = (e: React.FormEvent) => {
  e.preventDefault();
  setEditError("");
  if (!editTask) return;

  const errs = validateEdit(editTask);
  setFieldErrors(errs);
  if (Object.keys(errs).length) return; // stop if invalid

  setEditLoading(true);
  const body = statusOnly
    ? { status: canonicalTaskStatus(editTask.status) }
    : {
        subject: editTask.subject.trim(),
        Description: editTask.Description?.trim() ?? "",
        priority: editTask.priority,
        status: canonicalTaskStatus(editTask.status),
        weightage: Number(editTask.weightage),
        start_date: toIso(editTask.start_date),
        end_date: toIso(editTask.end_date),
        assign_to: Number(editTask.assign_to),
        tags: editTask.tags,
      };
  WebService.patchAPI({
    action: `/v1/door-management/tasks/${editTask.id}`,
    body,
  })
    .then((res: any) => {
      toast.success("Task updated successfully!");
      refreshTasks?.();
      const updated = res?.data || { ...editTask, status: canonicalTaskStatus(editTask.status) };
      setTask({
        ...editTask,
        ...updated,
        status: canonicalTaskStatus(updated.status || editTask.status),
        Description: updated.Description || updated.description || editTask.Description,
      });
      onClose();
    })
    .catch((err: any) => {
      setEditError(typeof err === "string" ? err : err?.message || "Failed to update task.");
    })
    .finally(() => setEditLoading(false));
};
  // Comment or Attachment
  const handleAddComment = async () => {
    if ((!comment.trim() && !commentFile) || !task || !canComment) return;
    setCommentLoading(true);

    try {
      const formData = new FormData();
      formData.append("task_id", String(task.id));
      formData.append("comment", comment.trim() || commentFile?.name || "Attachment");
      formData.append("commented_by", String(task.created_by_username)); 
      if (commentFile) formData.append("image", commentFile);

      await WebService.postAPI({
        action: "/v1/door-management/comment",
        body: formData,
        isFormData: true,
      });
      toast.success(commentFile && !comment.trim() ? "Attachment uploaded!" : "Comment added!");

      setTask((prev) => {
        if (!prev) return prev;
        const url = commentFile ? URL.createObjectURL(commentFile) : undefined;
        const filename = commentFile ? commentFile.name : undefined;
        if (commentFile && isImage(filename)) {
          return {
            ...prev,
            images: [
              ...(prev.images || []),
              { file_name: filename!, url: url },
            ],
            comments: comment.trim()
              ? [
                  ...(prev.comments || []),
                  {
                    comment_id: Math.random(),
                    task_id: prev.id,
                    comment,
                    commented_by: 0,
                    commented_by_username: "You",
                    image: url,
                    file_name: filename,
                  },
                ]
              : prev.comments,
          };
        }
        if (commentFile && !isImage(filename)) {
          return {
            ...prev,
            comments: [
              ...(prev.comments || []),
              {
                comment_id: Math.random(),
                task_id: prev.id,
                comment,
                commented_by: 0,
                commented_by_username: "You",
                image: undefined,
                file_name: filename,
              },
            ],
          };
        }
        return {
          ...prev,
          comments: [
            ...(prev.comments || []),
            {
              comment_id: Math.random(),
              task_id: prev.id,
              comment,
              commented_by: 0,
              commented_by_username: "You",
            },
          ],
        };
      });
      setComment("");
      setCommentFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch {
      toast.error("Failed to add comment/attachment");
    } finally {
      setCommentLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "—";
    const d = new Date(dateStr);
    return isNaN(d.getTime()) ? dateStr : d.toLocaleDateString();
  };



function parseTimeSpent(hours: number) {
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (isNaN(hours)) return "—";
  return `${h}h${m > 0 ? ` ${m}m` : ""}`;
}
  // const assigneeOptions = employees.map((emp) => ({
  //   value: emp.user_id,
  //   label: emp.username,
  // }));
if (!canRead) {
  return (
    <div className={styles.overlay}>
      <div className={styles.panel}>
        <div style={{ color: "#e43e2d", textAlign: "center", padding: "32px 0" }}>
          Access Denied: You do not have permission to view task details.
        </div>
        <CrossButton onClick={onClose} className={styles.closeBtn} />
      </div>
    </div>
  );
}
  return (
    <div className={styles.overlay}>
      <div className={styles.panel}>
     <CrossButton onClick={onClose} className={styles.closeBtn} />

        {loading ? (
          <div className={styles.center}>Loading...</div>
        ) : error ? (
          <div className={styles.center} style={{ color: "#e43e2d" }}>
            {error}
          </div>
        ) : !task ? (
          <div className={styles.center}>No data found.</div>
        ) : (
          <>
            {/* HEADER */}
            <div className={styles.headerRow}>
              <div>
                <div className={styles.titleRow}>
                  <span className={styles.title}>{task.subject}</span>
                  <span className={styles.taskIdPill}>{task.task_id}</span>
                </div>
                <div className={styles.subtitle}>
                  Create a new task by filling in the details below
                </div>
              </div>
            </div>

            {/* MAIN FORM */}
            <form
              className={styles.formGrid}
              autoComplete="off"
              onSubmit={isStatusEdit ? handleUpdateTask : (e) => e.preventDefault()}
            >
              {/* Row 1 */}
             <div className={styles.gridRow}>
  <div className={styles.inputGroup}>
    <label>Task title</label>
    <input
      className={styles.input}
      value={isFullEdit ? editTask?.subject || "" : task.subject}
      onChange={(e) => {
        if (!isFullEdit) return;
        clearErr?.("subject");
        setEditTask(editTask ? { ...editTask, subject: e.target.value } : null);
      }}
      disabled={!isFullEdit}
      style={fieldErrors?.subject ? { borderColor: "#e43e2d" } : {}}
    />
    {fieldErrors?.subject && (
      <div style={{ color: "#e43e2d", fontSize: 12, marginTop: 4 }}>
        {fieldErrors.subject}
      </div>
    )}
  </div>

  <div className={styles.inputGroup}>
    <label>Priority Level</label>
    {isFullEdit ? (
      <select
        className={styles.input}
        value={editTask?.priority}
        onChange={(e) =>
          setEditTask(
            editTask ? { ...editTask, priority: e.target.value as any } : null
          )
        }
      >
        <option value="HIGH">High</option>
        <option value="MEDIUM">Medium</option>
        <option value="LOW">Low</option>
      </select>
    ) : (
      <span className={`${styles.pill} ${priorityColors[task.priority || "LOW"]}`}>
        {task.priority.charAt(0) + task.priority.slice(1).toLowerCase()}
      </span>
    )}
  </div>

  <div className={styles.inputGroup}>
    <label>Weightage</label>
    {isFullEdit ? (
      <>
        <div className={styles.weightageControl}>
          <button
            type="button"
            onClick={() =>
              setEditTask(
                editTask
                  ? {
                      ...editTask,
                      weightage: Math.max(1, Number(editTask.weightage) - 1),
                    }
                  : null
              )
            }
            className={styles.weightageBtn}
          >
            -
          </button>
          <span className={styles.weightageNumber}>{editTask?.weightage || 1}</span>
          <button
            type="button"
            onClick={() => {
              clearErr?.("weightage");
              setEditTask(
                editTask
                  ? {
                      ...editTask,
                      weightage: Number(editTask.weightage) + 1,
                    }
                  : null
              );
            }}
            className={styles.weightageBtn}
          >
            +
          </button>
        </div>
        {fieldErrors?.weightage && (
          <div style={{ color: "#e43e2d", fontSize: 12, marginTop: 6 }}>
            {fieldErrors.weightage}
          </div>
        )}
      </>
    ) : (
      <span className={styles.weightageBadge}>{task.weightage}</span>
    )}
  </div>
</div>
              {/* Row 2 */}
<div className={styles.gridRow}>
  <div className={styles.inputGroupWide}>
    <label>Description</label>
    <textarea
      className={styles.input}
      value={isFullEdit ? editTask?.Description || "" : task.Description || ""}
      onChange={(e) => {
        if (!isFullEdit) return;
        clearErr?.("description");
        setEditTask(editTask ? { ...editTask, Description: e.target.value } : null);
      }}
      disabled={!isFullEdit}
      style={fieldErrors?.description ? { borderColor: "#e43e2d" } : {}}
    />
    {fieldErrors?.description && (
      <div style={{ color: "#e43e2d", fontSize: 12, marginTop: 4 }}>
        {fieldErrors.description}
      </div>
    )}
  </div>

  <div className={styles.inputGroup}>
    <label>Status</label>
    {isStatusEdit ? (
      <>
        <select
          className={`${styles.input} ${styles.statusSelect}`}
          value={canonicalTaskStatus(editTask?.status)}
          onChange={(e) => {
            clearErr?.("status");
            setEditTask(editTask ? { ...editTask, status: e.target.value as any } : null);
          }}
          style={fieldErrors?.status ? { borderColor: "#e43e2d" } : {}}
        >
          <option value="PENDING">Pending</option>
          <option value="PROCESSING">Processing</option>
          <option value="PAUSED">Paused</option>
          <option value="COMPLETED">Completed</option>
        </select>
        {fieldErrors?.status && (
          <div style={{ color: "#e43e2d", fontSize: 12, marginTop: 4 }}>
            {fieldErrors.status}
          </div>
        )}
      </>
    ) : (
      <span className={`${styles.pill} ${statusColors[canonicalTaskStatus(task.status)] || statusColors.PENDING}`}>
        {canonicalTaskStatus(task.status).replace(/_/g, " ")}
      </span>
    )}
  </div>
</div>

              {/* Row 3: Dates */}
              <div className={styles.gridRow}>
                <div className={styles.inputGroup}>
                  <label>Started date</label>
                  {isFullEdit ? (
                    <input
                      type="date"
                      className={styles.input}
                      value={editTask?.start_date?.slice(0, 10) || ""}
                      onChange={(e) =>
                        setEditTask(editTask ? { ...editTask, start_date: e.target.value } : null)
                      }
                    />
                  ) : (
                    <span className={styles.input}>{formatDate(task.start_date)}</span>
                  )}
                </div>
                <div className={styles.inputGroup}>
                  <label>End date</label>
                  {isFullEdit ? (
                    <input
                      type="date"
                      className={styles.input}
                      value={editTask?.end_date?.slice(0, 10) || ""}
                      onChange={(e) =>
                        setEditTask(editTask ? { ...editTask, end_date: e.target.value } : null)
                      }
                    />
                  ) : (
                    <span className={styles.input}>{formatDate(task.end_date)}</span>
                  )}
                </div>
              </div>
              {/* Row 4: Assignee */}
              <div className={styles.gridRow}>
                <div className={styles.inputGroup}>
                  <label>Assignee</label>
                  {isFullEdit ? (
                    <>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions
                        loadOptions={debouncedLoadEmployeeOptions}
                        onChange={(opt: any) => {
                          clearErr?.("assign_to");
                          setEditTask(editTask ? { ...editTask, assign_to: opt?.value ?? 0 } : null);
                        }}
                        defaultValue={
                          editTask?.assign_to
                            ? { value: editTask.assign_to, label: task?.assign_to_username || "—" }
                            : null
                        }
                        isClearable
                        classNamePrefix="react-select"
                        placeholder="Search Employee..."
                        noOptionsMessage={() => "Type to search employees"}
                        styles={{
                          control: (base) => ({
                            ...base,
                            minHeight: 36,
                            borderColor: fieldErrors?.assign_to ? "#e43e2d" : base.borderColor,
                            boxShadow: fieldErrors?.assign_to ? "0 0 0 1px #e43e2d" : base.boxShadow,
                          }),
                          valueContainer: (base) => ({ ...base, padding: "2px 8px" }),
                          indicatorsContainer: (base) => ({ ...base, paddingRight: 6 }),
                        }}
                      />
                      {fieldErrors?.assign_to && (
                        <div style={{ color: "#e43e2d", fontSize: 12, marginTop: 4 }}>
                          {fieldErrors.assign_to}
                        </div>
                      )}
                    </>
                  ) : (
                    <div className={styles.avatarRow}>
                      <span className={styles.stringAvatar}>{getInitials(task.assign_to_username)}</span>
                      <span className={styles.avatarLabel}>{task.assign_to_username}</span>
                    </div>
                  )}
                </div>

                <div className={styles.inputGroup}>
                  <label>Created by</label>
                  <div className={styles.avatarRow}>
                    <span className={styles.stringAvatar}>{getInitials(task.created_by_username)}</span>
                    <span className={styles.avatarLabel}>{task.created_by_username || "Unknown"}</span>
                  </div>
                </div>
              </div>

              <div className={styles.gridRow}>
                <div className={styles.inputGroup}>
                  <label>Total Time Spent (hours)</label>
                  <span className={styles.input}>
                    {parseTimeSpent(effectiveSpent)}
                    {task?.timer_running ? " (running)" : ""}
                  </span>
                </div>
                <div className={styles.inputGroup} />
                <div className={styles.inputGroup} />
              </div>

              {/* Progress */}
              <div className={styles.progressSection}>
                <div className={styles.progressLabelRow}>
                  <span>Total Time Spent</span>
                  <span>
                    {parseTimeSpent(effectiveSpent)} / {parseTimeSpent(capacity)}
                  </span>
                </div>

                <div className={styles.progressBarWrap}>
                  <div className={styles.progressBarBg} style={{ position: "relative" }}>
      {/* Yellow: up to capacity */}
      <div
        className={styles.progressBarFill}
        style={{
          width: `${pct}%`,
          background: "#FFD400",
          borderRadius: effectiveSpent <= capacity ? "6px" : "6px 0 0 6px",
          height: "100%",
          left: 0,
          top: 0,
          position: "absolute",
          zIndex: 2,
          transition: "width 0.22s cubic-bezier(.54,1.16,.54,.99)",
        }}
      />
      {/* Red: overflow beyond capacity */}
      {overflowPct > 0 && (
        <div
          className={styles.progressBarOverspent}
          style={{
            width: `${overflowPct}%`,
            height: "100%",
            position: "absolute",
            right: 0,
            top: 0,
            zIndex: 3,
                  background: "#ff4d4f",    
      borderRadius: "0 6px 6px 0"
          }}
        />
      )}
    </div>
  </div>
</div>




              {/* Attachments */}
              <div className={styles.attachmentsSection}>
                <label>Attachments</label>
<div className={styles.attachmentBox}>
  {(!task?.images || task.images.length === 0) ? (
    <span className={styles.noAttachment}>No attachments</span>
  ) : (
    task.images.map((img, i) => {
      const url = mediaUrl(img.url || img.file_name);
      const isImg = isImage(url) || isImage(img.file_name);

      if (isImg) {
        return (
          <img
            key={i}
            src={url}
            alt="attachment"
            className={styles.attachmentThumbImg}
            onClick={() => setImagePreviewUrl(url)}
            style={{
              width: 60,
              height: 60,
              objectFit: "cover",
              borderRadius: 7,
              marginRight: 12,
              background: "#f8f8f8",
              border: "2px solid #f4f4f4",
              boxShadow: "0 1px 8px #0002",
              cursor: "pointer",
            }}
          />
        );
      } else {
        return (
          <a
            key={i}
            href={url}
            download
            className={styles.attachmentFilePill}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: "#ffe56e",
              color: "#5c5100",
              borderRadius: 15,
              padding: "6px 14px",
              marginRight: 12,
              display: "inline-flex",
              alignItems: "center",
              textDecoration: "none",
              fontSize: 13,
              fontWeight: 500,
            }}
          >
            <FaPaperclip style={{ color: "#ceb400", marginRight: 5 }} />
            <FaRegFileAlt style={{ color: "#ceb400", marginRight: 5 }} />
            {img.file_name.split('/').pop()}
          </a>
        );
      }
    })
  )}
</div>





                {isFullEdit && (
                  <button
                    type="button"
                    className={styles.addAttachmentBtn}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <FaPaperclip style={{ marginRight: 8 }} />
                    Add attachment
                  </button>
                )}
                {/* Hidden file input for attachment */}
                <input
                  type="file"
                  accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                  style={{ display: "none" }}
                  ref={fileInputRef}
                  onChange={(e) =>
                    setCommentFile(e.target.files && e.target.files.length > 0 ? e.target.files[0] : null)
                  }
                  disabled={commentLoading}
                />
                {/* Upload preview */}
                {commentFile && isImage(commentFile.name) && (
                  <div className={styles.selectedFile}>
                    <img
                      src={URL.createObjectURL(commentFile)}
                      alt="preview"
                      onClick={() => setImagePreviewUrl(URL.createObjectURL(commentFile))}
                    />
                    <button
                      type="button"
                      onClick={() => setCommentFile(null)}
                      className={styles.removeFileBtn}
                    >
                      ×
                    </button>
                    <a
                      href={URL.createObjectURL(commentFile)}
                      download
                      className={styles.downloadLink}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <FaDownload style={{ fontSize: 13, marginRight: 2 }} />
                      Download
                    </a>
                  </div>
                )}
              </div>

              {/* COMMENTS */}
              <div className={styles.commentsSection}>
                <label>Comments & work updates</label>
                <div className={styles.commentsList}>
                  {(task.comments || []).length === 0 && (
                    <div className={styles.noComment}>No comments yet.</div>
                  )}
                  {(task.comments || []).map((com) => (
                    <div key={com.comment_id} className={styles.commentItem}>
                      <span className={styles.stringAvatarComment}>
                        {getInitials(com.commented_by_username)}
                      </span>
                       <div>
                         <span className={styles.commentTime}>
                          {formatDateTime(com.created_at)}
                          </span>
                        <div className={styles.commentAuthor}>
                          {com.commented_by_username || "User"}
                        </div>
                        <div className={styles.commentText}>{com.comment}</div>
                        {/* Images: capsule  */}
                        {com.image && isImage(com.file_name || com.image) && (
                          <span
                            className={styles.commentCapsule}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              borderRadius: 18,
                              background: "#f8f8f8",
                              padding: "2px 11px 2px 8px",
                              marginTop: 7,
                              marginBottom: 1,
                              boxShadow: "0 1px 4px #0001",
                              cursor: "pointer",
                            }}
                            onClick={() => setImagePreviewUrl(getCommentImageUrl(com.image))}
                            title="Click to preview"
                          >
                            <FaPaperclip style={{ color: "#ceb400", marginRight: 7, fontSize: 15 }} />
                            <img
                              src={getCommentImageUrl(com.image)}
                              alt="attachment"
                              style={{
                                width: 35,
                                height: 35,
                                objectFit: "cover",
                                borderRadius: 8,
                                background: "#f8f8f8",
                                border: "1px solid #ececec",
                              }}
                            />
                          </span>
                        )}
                        {/* Non-image file as yellow pill */}
                        {com.file_name && !isImage(com.file_name) && (
                          <span
                            className={styles.commentAttachment}
                            style={{
                              display: "inline-block",
                              marginTop: 6,
                            }}
                          >
                            <FaPaperclip style={{ color: "#ceb400", marginRight: 6, fontSize: 15 }} />
                            <a
                              href={com.image}
                              download={com.file_name}
                              className="filePill"
                              style={{
                                background: "#ffe56e",
                                color: "#5c5100",
                                textDecoration: "none",
                                padding: "5px 13px",
                                borderRadius: 13,
                                fontWeight: 500,
                                fontSize: 13,
                              }}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <FaRegFileAlt style={{ marginRight: 5, color: "#ceb400" }} />
                              {com.file_name}
                            </a>
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {/* COMMENT INPUT */}
                {canComment && (
                  <div className={styles.addCommentRow}>
                    <input
                      type="text"
                      className={styles.input}
                      placeholder="Add a work update..."
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      disabled={commentLoading}
                      style={{ flex: 1 }}
                    />
                    <button
                      type="button"
                      className={styles.attachBtn}
                      onClick={() => fileInputRef.current?.click()}
                      disabled={commentLoading}
                      title="Attach file"
                    >
                      <FaPaperclip />
                    </button>
                    <button
                      type="button"
                      className={styles.sendCommentBtn}
                      onClick={handleAddComment}
                      disabled={commentLoading || (!comment.trim() && !commentFile)}
                      title="Send"
                    >
                      <FaPaperPlane />
                    </button>
                  </div>
                )}
              </div>

              {/* SAVE BUTTON */}
              {isStatusEdit && (
                <button
                  type="submit"
                  className={styles.saveBtn}
                  disabled={editLoading}
                >
                  {editLoading ? "Saving..." : "Save"}
                </button>
              )}
              {editError && <div className={styles.editError}>{editError}</div>}
            </form>
            {/* Lightbox Image Preview */}
{imagePreviewUrl && (
  <div className={styles.lightboxOverlay} onClick={() => setImagePreviewUrl(null)}>
            se
    <img
      src={imagePreviewUrl}
      className={styles.lightboxImage}
      alt="Preview"
      style={{ maxWidth: "96vw", maxHeight: "80vh", borderRadius: 14 }}
    />
<a
  href={imagePreviewUrl}
  download
  target="_blank"
  rel="noopener noreferrer"
  className={styles.downloadButtonOverlay}
  onClick={e => e.stopPropagation()}
>
  <FaDownload style={{ marginRight: 5 }} /> Download
</a>

  </div>
)}

          </>
        )}
      </div>
    </div>
  );
};

export default TaskDetailPanel;
